import { Injectable, inject } from "@angular/core"
import { BehaviorSubject, Observable, skip } from "rxjs"
import { ApiService } from "./api.service"
import { AvailabilityService } from "./availability.service"
import { MessageService } from "primeng/api"
import { INITIAL_ADDRESSES_DATA } from "../meta/initial-addresses-data.const"

@Injectable({
  providedIn: "root"
})
export class AddressesService {
  private readonly apiService = inject(ApiService)
  private readonly availabilityService = inject(AvailabilityService)
  private readonly messageService = inject(MessageService)

  private readonly _isBusy$ = new BehaviorSubject<boolean>(false)
  public readonly isBusy$ = this._isBusy$.asObservable()

  private readonly _postalCodes$ = new BehaviorSubject<string[]>(INITIAL_ADDRESSES_DATA.postalCodes)
  private readonly _postalCode$ = new BehaviorSubject<string>(INITIAL_ADDRESSES_DATA.postalCode)

  public readonly postalCodes$: Observable<string[]> = this._postalCodes$.asObservable()
  public readonly postalCode$ = this._postalCode$.asObservable()

  private readonly _cities$ = new BehaviorSubject<string[]>(INITIAL_ADDRESSES_DATA.cities)
  private readonly _city$ = new BehaviorSubject<string>(INITIAL_ADDRESSES_DATA.city)

  public readonly cities$ = this._cities$.asObservable()
  public readonly city$ = this._city$.asObservable()

  private readonly _streetNames$ = new BehaviorSubject<string[]>(INITIAL_ADDRESSES_DATA.streetNames)
  private readonly _streetName$ = new BehaviorSubject<string>(INITIAL_ADDRESSES_DATA.streetName)

  public readonly streetNames$ = this._streetNames$.asObservable()
  public readonly streetName$ = this._streetName$.asObservable()

  private readonly _streetNumbers$ = new BehaviorSubject<string[]>(INITIAL_ADDRESSES_DATA.streetNumbers)
  private readonly _streetNumber$ = new BehaviorSubject<string>(INITIAL_ADDRESSES_DATA.streetNumber)

  public readonly streetNumbers$ = this._streetNumbers$.asObservable()
  public readonly streetNumber$ = this._streetNumber$.asObservable()

  private readonly _initialAddressFetched$ = new BehaviorSubject<boolean>(false)
  public readonly initialAddressFetched$ = this._initialAddressFetched$.asObservable().pipe(skip(1))

  constructor() {
    this.fetchPostalCodes()
  }

  private fetchPostalCodes(): void {
    this._isBusy$.next(true)
    this.apiService.fetchPostalCodes().subscribe({
      next: (postalCodes) => {
        this._postalCodes$.next(postalCodes)
        this._isBusy$.next(false)
      },
      error: () => {
        this.messageService.add({ severity: "error", summary: "Fehler", detail: "Postleitzahlen abfragen fehlgeschlagen" })
        this._isBusy$.next(false)
      }
    })
  }

  private fetchCities(): void {
    this._isBusy$.next(true)
    this.apiService.fetchCities(this._postalCode$.getValue()).subscribe({
      next: (cities) => {
        this._cities$.next(cities)
        this._isBusy$.next(false)
      },
      error: () => {
        this.messageService.add({ severity: "error", summary: "Fehler", detail: "Orte abfragen fehlgeschlagen" })
        this._isBusy$.next(false)
      }
    })
  }

  private fetchStreetNames(): void {
    this._isBusy$.next(true)
    this.apiService.fetchStreetNames(this._postalCode$.getValue(), this._city$.getValue()).subscribe({
      next: (streetNames) => {
        this._streetNames$.next(streetNames)
        this._isBusy$.next(false)
      },
      error: () => {
        this.messageService.add({ severity: "error", summary: "Fehler", detail: "Straßen abfragen fehlgeschlagen" })
        this._isBusy$.next(false)
      }
    })
  }

  private fetchStreetNumbers(): void {
    this._isBusy$.next(true)
    this.apiService
      .fetchStreetNumbers(this._postalCode$.getValue(), this._city$.getValue(), this._streetName$.getValue())
      .subscribe({
        next: (streetNumbers) => {
          this._streetNumbers$.next(streetNumbers)
          this._isBusy$.next(false)
        },
        error: () => {
          this.messageService.add({ severity: "error", summary: "Fehler", detail: "Hausnummern abfragen fehlgeschlagen" })
          this._isBusy$.next(false)
        }
      })
  }

  public setPostalCode(postalCode: string): void {
    if (postalCode !== this._postalCode$.getValue()) {
      this.availabilityService.reset()
      this._postalCode$.next(postalCode)
      this._cities$.next([])
      this.setCity("")
      if (postalCode) {
        this.fetchCities()
      }
    }
  }

  public setCity(city: string): void {
    if (city !== this._city$.getValue()) {
      this.availabilityService.reset()
      this._city$.next(city)
      this._streetNames$.next([])
      this.setStreetName("")
      if (city) {
        this.fetchStreetNames()
      }
    }
  }

  public setStreetName(streetName: string): void {
    if (streetName !== this._streetName$.getValue()) {
      this.availabilityService.reset()
      this._streetName$.next(streetName)
      this._streetNumbers$.next([])
      this.setStreetNumber("")
      if (streetName) {
        this.fetchStreetNumbers()
      }
    }
  }

  public setStreetNumber(streetNumber: string): void {
    this.availabilityService.reset()
    if (streetNumber !== this._streetNumber$.getValue()) {
      this._streetNumber$.next(streetNumber)
    }
  }

  public getPostalCode(): string {
    return this._postalCode$.getValue()
  }

  public getCity(): string {
    return this._city$.getValue()
  }

  public getStreetName(): string {
    return this._streetName$.getValue()
  }

  public getStreetNumber(): string {
    return this._streetNumber$.getValue()
  }

  public fetchInitialAddress(addressId: number): void {
    this._isBusy$.next(true)
    this.apiService.fetchAddress(addressId).subscribe({
      next: (res: any) => {
        this._postalCode$.next(res.postalCodes)
        this._cities$.next(res.cities)
        this._streetNames$.next(res.streetNames)
        this._streetNumbers$.next(res.streetNumbers)
        this._postalCode$.next(res.address.postalCode)
        this._city$.next(res.address.city)
        this._streetName$.next(res.address.streetName)
        this._streetNumber$.next(res.address.streetNumber)
        this._initialAddressFetched$.next(true)
        this._isBusy$.next(false)
      },
      error: () => {
        this.messageService.add({ severity: "error", summary: "Fehler", detail: "Adresse nicht gefunden" })
        this._isBusy$.next(false)
      }
    })
  }

}
